import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  Lock,
  Unlock,
  ScanBarcode,
  Ban,
  MapPin,
  FileDown,
  Printer,
  Undo2,
  PackageX,
  Layers,
  Star,
  Banknote,
  CreditCard,
  Repeat,
  Smartphone,
  HandCoins,
  Pencil,
  Check,
  UserPlus,
  Save,
  Eraser,
  Keyboard,
  Receipt,
  DollarSign,
  Percent,
  TrendingDown,
  TrendingUp,
  SplitSquareHorizontal,
  X,
} from "lucide-react";
import { useProducts, usePromotions, useBranches, useCategories, BranchRecord, ProductRecord } from "@/api/inventory";
import {
  useOpenShift,
  useOpenCashShift,
  useCloseCashShift,
  useCreateSale,
  useSales,
  useVoidSale,
  useStoreCustomers,
  useSaleReturns,
  downloadSaleReceipt,
  StoreCustomerRecord,
  PAYMENT_METHOD_LABEL,
  PaymentMethod,
  useCashMovements,
  useRegisterCashMovement,
  CASH_MOVEMENT_TYPE_LABEL,
  CashMovementType,
  SALE_PAYMENT_METHOD_OPTIONS,
  SalePaymentLine,
  useCashShiftBreakdown,
} from "@/api/pos";
import { useStoreSalesReport } from "@/api/reports";
import { apiClient, getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import { resolvePromotion, discountPerUnit } from "@/utils/pricing";
import { ProductImage } from "@/components/common/ProductImage";
import type { ApiSuccessResponse } from "@erp/shared";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });
const compactCurrency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0, notation: "compact" });

// Franja de color por categoría (puramente decorativo, para que la grilla de
// productos no se vea toda igual) — orden fijo, se asigna por posición de
// la categoría en la lista, nunca por contenido del nombre.
const CATEGORY_ACCENTS = ["bg-brand-500", "bg-blue-500", "bg-purple-500", "bg-amber-500", "bg-teal-500", "bg-rose-500", "bg-indigo-500", "bg-lime-600"];

const PAYMENT_METHOD_ICON: Record<PaymentMethod, typeof Banknote> = {
  EFECTIVO: Banknote,
  TARJETA: CreditCard,
  TRANSFERENCIA: Repeat,
  NEQUI: Smartphone,
  DAVIPLATA: Smartphone,
  CHEQUE: Repeat,
  OTRO: DollarSign,
  CREDITO: HandCoins,
  MIXTO: SplitSquareHorizontal,
};
const QUICK_PAYMENT_METHODS: PaymentMethod[] = ["EFECTIVO", "TARJETA", "TRANSFERENCIA", "NEQUI", "DAVIPLATA", "CREDITO"];

/** Botones rápidos de "monto recibido" en efectivo: el total exacto + los siguientes redondeos comunes de billetes en pesos colombianos, sin repetir el mismo valor dos veces. */
function suggestedCashAmounts(total: number): number[] {
  if (total <= 0) return [];
  const steps = [1000, 2000, 5000, 10000, 20000, 50000, 100000];
  const amounts = new Set<number>([Math.ceil(total)]);
  for (const step of steps) {
    const rounded = Math.ceil(total / step) * step;
    if (rounded > total) amounts.add(rounded);
    if (amounts.size >= 5) break;
  }
  return Array.from(amounts).sort((a, b) => a - b).slice(0, 5);
}

interface CartLine {
  product: ProductRecord;
  quantity: number;
}

interface SuspendedSale {
  id: string;
  createdAt: string;
  customerId: string | null;
  customerName: string;
  lines: Array<{ productId: string; quantity: number }>;
}

const POS_BRANCH_STORAGE_KEY = "pos.branchId";
const POS_FAVORITES_KEY = "pos.favoriteProductIds";

function loadFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(POS_FAVORITES_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function suspendedSalesKey(branchId: string) {
  return `pos.suspendedSales.${branchId}`;
}

function loadSuspendedSales(branchId: string): SuspendedSale[] {
  try {
    const raw = localStorage.getItem(suspendedSalesKey(branchId));
    return raw ? (JSON.parse(raw) as SuspendedSale[]) : [];
  } catch {
    return [];
  }
}

/** Punto de venta (POS): elegir sucursal, abrir/cerrar caja y registrar ventas descontando inventario de esa sucursal en tiempo real. */
export default function PosPage() {
  const { data: branches, isLoading: loadingBranches } = useBranches(true);
  const [branchId, setBranchId] = useState<string>(() => localStorage.getItem(POS_BRANCH_STORAGE_KEY) ?? "");

  useEffect(() => {
    if (!branches || branches.length === 0) return;
    if (branchId && branches.some((b) => b.id === branchId)) return;
    const fallback = branches.find((b) => b.isMain)?.id ?? branches[0].id;
    setBranchId(fallback);
  }, [branches, branchId]);

  const handleBranchChange = (id: string) => {
    setBranchId(id);
    localStorage.setItem(POS_BRANCH_STORAGE_KEY, id);
  };

  const { data: shift, isLoading: loadingShift } = useOpenShift(branchId || undefined);

  if (loadingBranches) {
    return <div className="card p-6 text-sm text-gray-400">Cargando...</div>;
  }

  if (!branches || branches.length === 0) {
    return <div className="card p-6 text-sm text-gray-400">No hay sucursales configuradas todavía.</div>;
  }

  if (!branchId || loadingShift) {
    return <div className="card p-6 text-sm text-gray-400">Cargando...</div>;
  }

  if (!shift) {
    return <OpenShiftPanel branches={branches} branchId={branchId} onBranchChange={handleBranchChange} />;
  }

  const branchName = branches.find((b) => b.id === branchId)?.name ?? "";
  return <SaleScreen shiftId={shift.id} branchId={branchId} branchName={branchName} openingCash={shift.openingCash} openedAt={shift.openedAt} openedByName={shift.openedBy?.fullName ?? ""} />;
}

function OpenShiftPanel({ branches, branchId, onBranchChange }: { branches: BranchRecord[]; branchId: string; onBranchChange: (id: string) => void }) {
  const openShift = useOpenCashShift();
  const [openingCash, setOpeningCash] = useState("0");

  const handleOpen = async () => {
    try {
      await openShift.mutateAsync({ branchId, openingCash });
      toast.success("Turno de caja abierto.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="mx-auto max-w-sm space-y-4 text-center">
      <div className="card space-y-4 p-6">
        <Lock className="mx-auto text-gray-400" size={32} />
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Caja cerrada</h1>
          <p className="text-sm text-gray-500">Elige la sucursal y abre un turno de caja para empezar a registrar ventas.</p>
        </div>
        {branches.length > 1 && (
          <div>
            <label className="flex items-center justify-center gap-1 text-left text-sm font-medium text-gray-700">
              <MapPin size={14} className="text-gray-400" /> Sucursal
            </label>
            <select className="input-field mt-1" value={branchId} onChange={(e) => onBranchChange(e.target.value)}>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-left text-sm font-medium text-gray-700">Efectivo inicial en caja</label>
          <input className="input-field mt-1" type="number" value={openingCash} onChange={(e) => setOpeningCash(e.target.value)} />
        </div>
        <button type="button" className="btn-primary w-full" disabled={openShift.isPending} onClick={handleOpen}>
          <Unlock size={16} className="mr-1.5" /> {openShift.isPending ? "Abriendo..." : "Abrir turno de caja"}
        </button>
      </div>
    </div>
  );
}

function SaleScreen({
  shiftId,
  branchId,
  branchName,
  openingCash,
  openedAt,
  openedByName,
}: {
  shiftId: string;
  branchId: string;
  branchName: string;
  openingCash: string;
  openedAt: string;
  openedByName: string;
}) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [scanCode, setScanCode] = useState("");
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef<HTMLInputElement>(null);
  const customerInputRef = useRef<HTMLInputElement>(null);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [showCashMovement, setShowCashMovement] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const user = useAuthStore((s) => s.user);
  const canVoidSale = hasPermission("pos.delete");
  const voidSale = useVoidSale();

  const [favorites, setFavorites] = useState<Set<string>>(() => loadFavorites());
  const toggleFavorite = (productId: string) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      localStorage.setItem(POS_FAVORITES_KEY, JSON.stringify(Array.from(next)));
      return next;
    });
  };

  const [suspended, setSuspended] = useState<SuspendedSale[]>(() => loadSuspendedSales(branchId));
  const [showSuspended, setShowSuspended] = useState(false);
  const persistSuspended = (list: SuspendedSale[]) => {
    setSuspended(list);
    localStorage.setItem(suspendedSalesKey(branchId), JSON.stringify(list));
  };

  // Checkout inline (en el panel del carrito, no en un modal aparte).
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("EFECTIVO");
  const [amountPaid, setAmountPaid] = useState("0");
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [manualDiscount, setManualDiscount] = useState(0);
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerName, setCustomerName] = useState("");
  const { data: customerMatches } = useStoreCustomers(customerSearch.length >= 2 ? customerSearch : undefined);
  const [creditInstallments, setCreditInstallments] = useState("1");
  const [creditFirstDueDate, setCreditFirstDueDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  });
  const [creditIntervalDays, setCreditIntervalDays] = useState("30");
  const isCredit = paymentMethod === "CREDITO";
  // Pago mixto: 2+ métodos en la misma venta (ej. mitad efectivo, mitad tarjeta) — modo aparte,
  // mutuamente excluyente con crédito. Ver pos.service.ts::createSale / SalePayment.
  const [showMixedPayment, setShowMixedPayment] = useState(false);
  const [mixedPayments, setMixedPayments] = useState<SalePaymentLine[]>([]);
  const createSale = useCreateSale();

  const { data: categories } = useCategories(true);
  const categoryIndexById = useMemo(() => new Map((categories ?? []).map((c, i) => [c.id, i])), [categories]);

  const { data: productsResponse } = useProducts({ search: search || undefined, categoryId: categoryFilter || undefined, isActive: true, pageSize: 60, branchId });
  const products = productsResponse?.data ?? [];
  const { data: promotions } = usePromotions(true);
  const { data: salesResponse } = useSales({ cashShiftId: shiftId, pageSize: 15 });
  const recentSales = salesResponse?.data ?? [];

  // Estadísticas del día (reutiliza el mismo reporte de reportes/ventas — no
  // duplica lógica de agregación). Se oculta silenciosamente si el usuario
  // no tiene permiso de reportes, en vez de romper la pantalla de venta.
  const canSeeReports = hasPermission("reports.read");
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const { data: dailyReport } = useStoreSalesReport({ dateFrom: today, dateTo: today, branchId, enabled: canSeeReports });
  const { data: returnsResponse } = useSaleReturns({ pageSize: 50 });
  const todaysReturnsTotal = useMemo(() => {
    if (!returnsResponse?.data) return 0;
    return returnsResponse.data
      .filter((r) => r.branchId === branchId && r.createdAt.slice(0, 10) === today)
      .reduce((sum, r) => sum + Number(r.totalRefund), 0);
  }, [returnsResponse, branchId, today]);

  const paymentBreakdown = useMemo(() => {
    const rows = dailyReport?.byPaymentMethod ?? [];
    const byMethod = (m: string) => rows.find((r) => r.paymentMethod === m)?.total ?? 0;
    return {
      efectivo: byMethod("EFECTIVO"),
      tarjeta: byMethod("TARJETA"),
      transferencias: byMethod("TRANSFERENCIA") + byMethod("NEQUI") + byMethod("DAVIPLATA") + byMethod("CHEQUE"),
      credito: byMethod("CREDITO"),
    };
  }, [dailyReport]);

  const addToCart = (product: ProductRecord) => {
    // branchStockQty = saldo real de ESTA sucursal (viene solo cuando se
    // filtra por branchId); si por algún motivo no vino, se cae al agregado.
    const availableInBranch = Number(product.branchStockQty ?? product.stockQty);
    if (availableInBranch <= 0) {
      toast.error(`"${product.name}" no tiene stock disponible en ${branchName}.`);
      return;
    }
    setCart((prev) => {
      const existing = prev.find((l) => l.product.id === product.id);
      if (existing) return prev.map((l) => (l.product.id === product.id ? { ...l, quantity: l.quantity + 1 } : l));
      return [...prev, { product, quantity: 1 }];
    });
  };

  const handleScanSubmit = async () => {
    const code = scanCode.trim();
    if (!code) return;
    setScanning(true);
    try {
      const { data } = await apiClient.get<ApiSuccessResponse<ProductRecord[]>>("/inventory/products", {
        params: { search: code, isActive: true, pageSize: 5, branchId },
      });
      const results = data.data ?? [];
      const exact = results.find((p) => p.barcode === code) ?? (results.length === 1 ? results[0] : undefined);
      if (!exact) {
        toast.error(`Ningún producto activo con código "${code}".`);
      } else {
        addToCart(exact);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    } finally {
      setScanCode("");
      setScanning(false);
      scanInputRef.current?.focus();
    }
  };

  const handleVoidSale = async (saleId: string, saleNumber: number) => {
    if (!window.confirm(`¿Anular la venta #${saleNumber}? Esto revertirá el inventario descontado.`)) return;
    try {
      await voidSale.mutateAsync(saleId);
      toast.success(`Venta #${saleNumber} anulada.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const changeQuantity = (productId: string, delta: number) =>
    setCart((prev) => prev.map((l) => (l.product.id === productId ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l)).filter((l) => l.quantity > 0));

  const removeLine = (productId: string) => setCart((prev) => prev.filter((l) => l.product.id !== productId));

  const resetCheckoutFields = () => {
    setCart([]);
    setManualDiscount(0);
    setEditingDiscount(false);
    setCustomerId(null);
    setCustomerName("");
    setCustomerSearch("");
    setPaymentMethod("EFECTIVO");
    setShowMixedPayment(false);
    setMixedPayments([]);
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let promoDiscountTotal = 0;
    let taxTotal = 0;
    for (const line of cart) {
      const unitPrice = Number(line.product.salePrice);
      const lineSubtotal = unitPrice * line.quantity;
      const promotion = resolvePromotion(line.product, promotions);
      const lineDiscount = discountPerUnit(line.product, promotion) * line.quantity;
      subtotal += lineSubtotal;
      promoDiscountTotal += lineDiscount;
      taxTotal += (lineSubtotal - lineDiscount) * (Number(line.product.taxRate) / 100);
    }
    const discountTotal = Math.round((promoDiscountTotal + manualDiscount) * 100) / 100;
    return {
      subtotal: Math.round(subtotal * 100) / 100,
      promoDiscountTotal: Math.round(promoDiscountTotal * 100) / 100,
      discountTotal,
      taxTotal: Math.round(taxTotal * 100) / 100,
      total: Math.round((subtotal - discountTotal + taxTotal) * 100) / 100,
    };
  }, [cart, promotions, manualDiscount]);

  // Protección de margen: mismo cálculo que pos.service.ts::createSale (precio - descuento por
  // unidad, antes de impuesto, contra el costo actual del producto) — se muestra en el carrito
  // ANTES de cobrar, y bloquea el cobro hasta que el cajero confirme explícitamente en el modal
  // de abajo. El backend vuelve a validar esto de todas formas (nunca confía solo en el frontend).
  const belowCostLines = useMemo(() => {
    return cart
      .map((line) => {
        const promotion = resolvePromotion(line.product, promotions);
        const effectiveUnitPrice = Number(line.product.salePrice) - discountPerUnit(line.product, promotion);
        const unitCost = Number(line.product.costPrice);
        return { product: line.product, effectiveUnitPrice, unitCost, isBelowCost: effectiveUnitPrice < unitCost };
      })
      .filter((l) => l.isBelowCost);
  }, [cart, promotions]);

  const [showBelowCostConfirm, setShowBelowCostConfirm] = useState(false);

  useEffect(() => {
    if (!isCredit) setAmountPaid(String(totals.total));
    else if (Number(amountPaid) > totals.total) setAmountPaid("0");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.total, paymentMethod]);

  const change = Math.max(0, Math.round((Number(amountPaid) - totals.total) * 100) / 100);

  // Pago mixto: cuánto falta cubrir después de los métodos distintos a efectivo, y si el
  // efectivo declarado alcanza para cubrir ese resto (el mismo cálculo que hace el backend
  // en pos.service.ts::createSale — ver ese comentario para el porqué).
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const mixedNonCashTotal = round2(mixedPayments.filter((p) => p.paymentMethod !== "EFECTIVO").reduce((sum, p) => sum + Number(p.amount || 0), 0));
  const mixedCashLine = mixedPayments.find((p) => p.paymentMethod === "EFECTIVO");
  const mixedCashTendered = Number(mixedCashLine?.amount || 0);
  const mixedRemaining = Math.max(0, round2(totals.total - mixedNonCashTotal));
  const mixedChange = Math.max(0, round2(mixedCashTendered - mixedRemaining));
  const mixedValid =
    mixedPayments.length >= 2 && mixedPayments.every((p) => Number(p.amount) > 0) && mixedNonCashTotal <= totals.total + 0.01 && mixedCashTendered >= mixedRemaining - 0.01;

  const checkoutValid =
    cart.length > 0 && (showMixedPayment ? mixedValid : isCredit ? Number(amountPaid) <= totals.total && !!customerId : Number(amountPaid) >= totals.total);

  const selectCustomer = (customer: StoreCustomerRecord) => {
    setCustomerId(customer.id);
    setCustomerName(customer.name);
    setCustomerSearch("");
  };

  const submitSale = async (acknowledgeBelowCost: boolean) => {
    try {
      await createSale.mutateAsync({
        branchId,
        ...(showMixedPayment ? { amountPaid: 0, payments: mixedPayments } : { paymentMethod, amountPaid }),
        discountTotal: manualDiscount,
        storeCustomerId: customerId ?? undefined,
        lines: cart.map((l) => ({ productId: l.product.id, quantity: l.quantity })),
        ...(isCredit && !showMixedPayment ? { creditInstallments: Number(creditInstallments) || 1, creditFirstDueDate, creditIntervalDays: Number(creditIntervalDays) || 30 } : {}),
        ...(acknowledgeBelowCost ? { acknowledgeBelowCost: true } : {}),
      });
      toast.success(isCredit ? "Venta a crédito registrada." : "Venta registrada.");
      setShowBelowCostConfirm(false);
      resetCheckoutFields();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleCharge = async () => {
    if (cart.length === 0) return;
    if (!checkoutValid) {
      toast.error(isCredit ? "Elige un cliente; el abono inicial no puede ser mayor al total." : "El monto pagado no puede ser menor al total.");
      return;
    }
    if (belowCostLines.length > 0) {
      setShowBelowCostConfirm(true);
      return;
    }
    await submitSale(false);
  };

  const handleSuspendSale = () => {
    if (cart.length === 0) return;
    const entry: SuspendedSale = {
      id: `${Date.now()}`,
      createdAt: new Date().toISOString(),
      customerId,
      customerName,
      lines: cart.map((l) => ({ productId: l.product.id, quantity: l.quantity })),
    };
    persistSuspended([entry, ...suspended]);
    resetCheckoutFields();
    toast.success("Venta guardada localmente en este equipo. Puedes reanudarla desde \"Ventas guardadas\".");
  };

  const resumeSuspended = (entry: SuspendedSale) => {
    const restored: CartLine[] = [];
    let missing = 0;
    for (const line of entry.lines) {
      const product = products.find((p) => p.id === line.productId);
      if (product) restored.push({ product, quantity: line.quantity });
      else missing += 1;
    }
    setCart(restored);
    setCustomerId(entry.customerId);
    setCustomerName(entry.customerName);
    persistSuspended(suspended.filter((s) => s.id !== entry.id));
    setShowSuspended(false);
    if (missing > 0) toast.error(`${missing} producto(s) de esa venta guardada ya no están disponibles en esta pantalla y no se restauraron.`);
    else toast.success("Venta guardada restaurada al carrito.");
  };

  const discardSuspended = (id: string) => persistSuspended(suspended.filter((s) => s.id !== id));

  // Atajos de teclado: F2 buscar/escanear, F4 cliente, F8 cobrar, ESC cancelar.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "F2") {
        e.preventDefault();
        scanInputRef.current?.focus();
      } else if (e.key === "F4") {
        e.preventDefault();
        customerInputRef.current?.focus();
      } else if (e.key === "F8") {
        e.preventDefault();
        if (checkoutValid) handleCharge();
      } else if (e.key === "Escape") {
        setShowCloseShift(false);
        setShowSuspended(false);
        setEditingDiscount(false);
        setShowBelowCostConfirm(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutValid, cart, paymentMethod, amountPaid, customerId, manualDiscount]);

  const openedTime = openedAt ? new Date(openedAt).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200/80 bg-white px-4 py-3 shadow-sm">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Punto de Venta</h1>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1 font-medium text-green-600">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> En línea
            </span>
            <span className="flex items-center gap-1">
              <MapPin size={12} className="text-gray-400" /> {branchName}
            </span>
            {openedByName && <span>Turno abierto por: <strong className="text-gray-700">{openedByName}</strong></span>}
            {openedTime && <span>Inicio: {openedTime}</span>}
            <span>Saldo inicial: {currency.format(Number(openingCash))}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/store/returns" className="btn-secondary">
            <Undo2 size={16} className="mr-1.5" /> Devoluciones
          </Link>
          <Link to="/store/cash-shifts" className="btn-secondary">
            <Layers size={16} className="mr-1.5" /> Historial de cierres
          </Link>
          <button type="button" className="btn-secondary" onClick={() => setShowCashMovement(true)}>
            <Banknote size={16} className="mr-1.5" /> Entrada/salida de caja
          </button>
          <button type="button" className="btn-secondary" onClick={() => setShowCloseShift(true)}>
            <Lock size={16} className="mr-1.5" /> Cerrar caja
          </button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2 rounded-md border-2 border-brand-200 bg-brand-50/50 p-2">
            <ScanBarcode size={20} className="shrink-0 text-brand-600" />
            <input
              ref={scanInputRef}
              className="input-field flex-1 border-0 bg-transparent focus:ring-0"
              placeholder="Escanear código de barras o presionar Enter..."
              value={scanCode}
              disabled={scanning}
              autoFocus
              onChange={(e) => setScanCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleScanSubmit();
                }
              }}
            />
            <span className="hidden shrink-0 rounded border border-brand-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-brand-500 sm:inline">F2</span>
          </div>

          <input className="input-field w-full" placeholder="Buscar producto por nombre, SKU o código de barras..." value={search} onChange={(e) => setSearch(e.target.value)} />

          {categories && categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                  categoryFilter === "" ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"
                }`}
                onClick={() => setCategoryFilter("")}
              >
                Todos
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                    categoryFilter === c.id ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                  onClick={() => setCategoryFilter(c.id)}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between text-sm text-gray-500">
            <span>Productos ({products.length})</span>
          </div>

          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
            {products.map((p) => {
              const stockHere = Number(p.branchStockQty ?? p.stockQty);
              const inCart = cart.find((l) => l.product.id === p.id)?.quantity ?? 0;
              const accentIdx = p.categoryId ? categoryIndexById.get(p.categoryId) ?? 0 : 0;
              const promotion = resolvePromotion(p, promotions);
              const outOfStock = stockHere <= 0;
              const isFavorite = favorites.has(p.id);
              return (
                <div
                  key={p.id}
                  className={`card relative flex flex-col gap-1.5 overflow-hidden p-2.5 text-left transition ${
                    outOfStock ? "opacity-50" : "hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-md"
                  }`}
                >
                  <span className={`absolute inset-x-0 top-0 h-1 ${CATEGORY_ACCENTS[accentIdx % CATEGORY_ACCENTS.length]}`} />
                  <button
                    type="button"
                    className="absolute right-1.5 top-3 z-10 text-gray-300 hover:text-amber-400"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(p.id);
                    }}
                    aria-label="Favorito"
                    title="Favorito (guardado en este equipo)"
                  >
                    <Star size={15} className={isFavorite ? "fill-amber-400 text-amber-400" : ""} />
                  </button>
                  {inCart > 0 && (
                    <span className="absolute left-1.5 top-3 z-10 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-600 px-1 text-[11px] font-semibold text-white">
                      {inCart}
                    </span>
                  )}
                  <button type="button" disabled={outOfStock} className="mt-1 disabled:cursor-not-allowed" onClick={() => !outOfStock && addToCart(p)}>
                    <ProductImage productId={p.id} hasImage={p.hasImage} alt={p.name} className="h-16 w-full sm:h-20" iconSize={22} />
                  </button>
                  <button type="button" disabled={outOfStock} className="flex-1 text-left disabled:cursor-not-allowed" onClick={() => !outOfStock && addToCart(p)}>
                    <span className="line-clamp-2 text-sm font-medium text-gray-900">{p.name}</span>
                    <span className="mt-0.5 flex items-center gap-1 text-xs text-gray-400">
                      {outOfStock ? <PackageX size={12} className="text-red-400" /> : <Layers size={12} className="text-gray-300" />}
                      {outOfStock ? "Sin stock" : `Stock ${stockHere}`}
                    </span>
                  </button>
                  <div className="flex items-center justify-between gap-1">
                    <div className="flex items-center gap-1">
                      <span className="text-sm font-semibold text-brand-700">{currency.format(Number(p.salePrice))}</span>
                      {promotion && <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">Promo</span>}
                    </div>
                    <button
                      type="button"
                      disabled={outOfStock}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white hover:bg-brand-700 disabled:bg-gray-300"
                      onClick={() => addToCart(p)}
                      aria-label="Agregar"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                </div>
              );
            })}
            {products.length === 0 && <p className="col-span-full text-sm text-gray-400">Sin resultados.</p>}
          </div>

          <div className="card overflow-hidden">
            <div className="flex items-center justify-between border-b border-gray-100 p-4">
              <p className="text-sm font-semibold text-gray-900">Ventas de este turno</p>
              <Link to="/store/sales-history" className="text-xs font-medium text-brand-600 hover:text-brand-800">
                Ver todas las ventas
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">#</th>
                    <th className="px-3 py-2 text-left font-medium">Tipo</th>
                    <th className="px-3 py-2 text-left font-medium">Hora</th>
                    <th className="px-3 py-2 text-left font-medium">Cliente</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                    <th className="px-3 py-2 text-left font-medium">Método</th>
                    <th className="px-3 py-2 text-left font-medium">Estado</th>
                    <th className="px-3 py-2 text-right font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recentSales.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">#{s.saleNumber}</td>
                      <td className="px-3 py-2 text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Receipt size={12} className="text-gray-300" /> Venta
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-500">{new Date(s.saleDate).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-3 py-2 text-gray-500">{s.storeCustomer?.name ?? "Consumidor final"}</td>
                      <td className={`px-3 py-2 text-right ${s.status === "ANULADA" ? "text-red-600 line-through" : "text-gray-900"}`}>{currency.format(Number(s.total))}</td>
                      <td className="px-3 py-2 text-gray-500">{PAYMENT_METHOD_LABEL[s.paymentMethod]}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            s.status === "ANULADA" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                          }`}
                        >
                          {s.status === "ANULADA" ? "Anulada" : "Completada"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <button type="button" title="Descargar recibo (PDF)" className="text-gray-400 hover:text-brand-600" onClick={() => downloadSaleReceipt(s.id, s.saleNumber)}>
                            <FileDown size={14} />
                          </button>
                          <button
                            type="button"
                            title="Imprimir"
                            className="text-gray-400 hover:text-brand-600"
                            onClick={async () => {
                              await downloadSaleReceipt(s.id, s.saleNumber);
                              toast("Recibo descargado. Ábrelo para imprimirlo.", { icon: "🖨️" });
                            }}
                          >
                            <Printer size={14} />
                          </button>
                          {s.status === "COMPLETADA" && (
                            <Link to={`/store/returns?sale=${s.saleNumber}`} title="Devolver productos" className="text-gray-400 hover:text-brand-600">
                              <Undo2 size={14} />
                            </Link>
                          )}
                          {s.status === "COMPLETADA" && canVoidSale && (
                            <button
                              type="button"
                              title="Anular venta"
                              className="text-gray-400 hover:text-red-600 disabled:opacity-40"
                              disabled={voidSale.isPending}
                              onClick={() => handleVoidSale(s.id, s.saleNumber)}
                            >
                              <Ban size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {recentSales.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-3 py-4 text-center text-gray-400">
                        Sin ventas todavía.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {canSeeReports && dailyReport && (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4 lg:grid-cols-7">
              <StatCard icon={TrendingUp} color="brand" label="Ventas del día" value={compactCurrency.format(dailyReport.kpis.total)} sub={`${dailyReport.kpis.salesCount} ventas`} />
              <StatCard icon={Banknote} color="green" label="Efectivo" value={compactCurrency.format(paymentBreakdown.efectivo)} sub={pct(paymentBreakdown.efectivo, dailyReport.kpis.total)} />
              <StatCard icon={CreditCard} color="blue" label="Tarjeta" value={compactCurrency.format(paymentBreakdown.tarjeta)} sub={pct(paymentBreakdown.tarjeta, dailyReport.kpis.total)} />
              <StatCard icon={Repeat} color="purple" label="Transferencias" value={compactCurrency.format(paymentBreakdown.transferencias)} sub={pct(paymentBreakdown.transferencias, dailyReport.kpis.total)} />
              <StatCard icon={HandCoins} color="amber" label="Crédito" value={compactCurrency.format(paymentBreakdown.credito)} sub={pct(paymentBreakdown.credito, dailyReport.kpis.total)} />
              <StatCard icon={TrendingDown} color="red" label="Devoluciones" value={compactCurrency.format(todaysReturnsTotal)} sub={pct(todaysReturnsTotal, dailyReport.kpis.total)} />
              <StatCard
                icon={Percent}
                color="teal"
                label="Utilidad estimada"
                value={compactCurrency.format(dailyReport.kpis.profitEstimate)}
                sub={`${dailyReport.kpis.marginPercent.toFixed(1)}%`}
              />
            </div>
          )}
        </div>

        {/* ---------- Carrito / cobro ---------- */}
        <div className="card flex flex-col p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart size={18} className="text-brand-600" />
              <p className="font-semibold text-gray-900">Carrito de compras</p>
            </div>
            <div className="flex items-center gap-2">
              {suspended.length > 0 && (
                <button type="button" className="relative text-gray-400 hover:text-brand-600" title="Ventas guardadas" onClick={() => setShowSuspended((v) => !v)}>
                  <Save size={16} />
                  <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-600 px-1 text-[9px] font-semibold text-white">
                    {suspended.length}
                  </span>
                </button>
              )}
              <button type="button" className="text-gray-400 hover:text-red-600" title="Vaciar carrito" onClick={resetCheckoutFields}>
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          {showSuspended && (
            <div className="mb-3 max-h-40 space-y-1.5 overflow-y-auto rounded-md border border-gray-100 bg-gray-50 p-2">
              {suspended.length === 0 && <p className="text-xs text-gray-400">No hay ventas guardadas.</p>}
              {suspended.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-2 rounded-md bg-white px-2 py-1.5 text-xs shadow-sm">
                  <div className="min-w-0">
                    <p className="truncate text-gray-700">{s.customerName || "Sin cliente"} · {s.lines.length} ítem(s)</p>
                    <p className="text-gray-400">{new Date(s.createdAt).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</p>
                  </div>
                  <div className="flex shrink-0 gap-1.5">
                    <button type="button" className="font-medium text-brand-600 hover:text-brand-800" onClick={() => resumeSuspended(s)}>
                      Reanudar
                    </button>
                    <button type="button" className="font-medium text-red-500 hover:text-red-700" onClick={() => discardSuspended(s.id)}>
                      Descartar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="max-h-64 flex-1 space-y-2 overflow-y-auto">
            {cart.map((line) => {
              const promotion = resolvePromotion(line.product, promotions);
              const isBelowCost = belowCostLines.some((l) => l.product.id === line.product.id);
              return (
                <div key={line.product.id} className={`flex items-center gap-2 rounded-md border p-2 ${isBelowCost ? "border-red-200 bg-red-50/50" : "border-gray-100"}`}>
                  <ProductImage productId={line.product.id} hasImage={line.product.hasImage} alt={line.product.name} className="h-9 w-9" iconSize={14} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{line.product.name}</p>
                    <p className="truncate text-xs text-gray-400">
                      Código: {line.product.barcode || line.product.sku} · {currency.format(Number(line.product.salePrice))} c/u
                      {promotion && <span className="ml-1.5 rounded-full bg-green-100 px-1.5 py-0.5 text-[10px] font-medium text-green-700">Promo</span>}
                      {isBelowCost && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700">
                          <TrendingDown size={10} /> Bajo costo
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button type="button" className="rounded border border-gray-200 p-1" onClick={() => changeQuantity(line.product.id, -1)}>
                      <Minus size={12} />
                    </button>
                    <span className="w-6 text-center text-sm">{line.quantity}</span>
                    <button type="button" className="rounded border border-gray-200 p-1" onClick={() => changeQuantity(line.product.id, 1)}>
                      <Plus size={12} />
                    </button>
                  </div>
                  <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => removeLine(line.product.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
            {cart.length === 0 && <p className="text-sm text-gray-400">Agrega productos haciendo clic en ellos.</p>}
          </div>

          <div className="mt-3 space-y-1 border-t border-gray-100 pt-3 text-sm">
            <div className="flex justify-between text-gray-500">
              <span>Subtotal</span>
              <span>{currency.format(totals.subtotal)}</span>
            </div>
            <div className="flex items-center justify-between text-gray-500">
              <span className="flex items-center gap-1">
                Descuento
                <button type="button" className="text-gray-300 hover:text-brand-600" onClick={() => setEditingDiscount((v) => !v)} title="Editar descuento">
                  <Pencil size={11} />
                </button>
              </span>
              {editingDiscount ? (
                <span className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    className="w-24 rounded border border-gray-200 px-1.5 py-0.5 text-right text-xs"
                    value={manualDiscount}
                    onChange={(e) => setManualDiscount(Math.max(0, Number(e.target.value) || 0))}
                  />
                  <button type="button" className="text-green-600" onClick={() => setEditingDiscount(false)}>
                    <Check size={13} />
                  </button>
                </span>
              ) : (
                <span className={totals.discountTotal > 0 ? "text-green-600" : ""}>{totals.discountTotal > 0 ? `-${currency.format(totals.discountTotal)}` : currency.format(0)}</span>
              )}
            </div>
            <div className="flex justify-between text-gray-500">
              <span>IVA</span>
              <span>{currency.format(totals.taxTotal)}</span>
            </div>
            <div className="flex justify-between text-base font-semibold text-gray-900">
              <span>Total</span>
              <span>{currency.format(totals.total)}</span>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {QUICK_PAYMENT_METHODS.map((method) => {
              const Icon = PAYMENT_METHOD_ICON[method];
              const active = paymentMethod === method;
              return (
                <button
                  key={method}
                  type="button"
                  className={`flex flex-col items-center gap-1 rounded-lg border px-2 py-2 text-[11px] font-medium transition ${
                    active ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    setPaymentMethod(method);
                    setShowMixedPayment(false);
                  }}
                >
                  <Icon size={16} />
                  {PAYMENT_METHOD_LABEL[method]}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className={`mt-1.5 flex w-full items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 text-[11px] font-medium transition ${
              showMixedPayment ? "border-brand-500 bg-brand-50 text-brand-700" : "border-dashed border-gray-300 text-gray-500 hover:bg-gray-50"
            }`}
            onClick={() => {
              setShowMixedPayment((v) => !v);
              if (!showMixedPayment && mixedPayments.length === 0) setMixedPayments([{ paymentMethod: "EFECTIVO", amount: "" }]);
            }}
          >
            <SplitSquareHorizontal size={14} />
            {showMixedPayment ? "Cancelar pago mixto" : "Pago mixto (2+ métodos)"}
          </button>

          {showMixedPayment && (
            <div className="mt-2 space-y-2 rounded-md bg-blue-50 p-2.5">
              {mixedPayments.map((line, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <select
                    className="input-field flex-1 text-xs"
                    value={line.paymentMethod}
                    onChange={(e) => setMixedPayments((prev) => prev.map((p, i) => (i === idx ? { ...p, paymentMethod: e.target.value as PaymentMethod } : p)))}
                  >
                    {SALE_PAYMENT_METHOD_OPTIONS.map((m) => (
                      <option key={m} value={m}>
                        {PAYMENT_METHOD_LABEL[m]}
                      </option>
                    ))}
                  </select>
                  <input
                    className="input-field w-24 text-xs"
                    type="number"
                    placeholder="Monto"
                    value={line.amount}
                    onChange={(e) => setMixedPayments((prev) => prev.map((p, i) => (i === idx ? { ...p, amount: e.target.value } : p)))}
                  />
                  <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => setMixedPayments((prev) => prev.filter((_, i) => i !== idx))} aria-label="Quitar">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="text-xs font-medium text-brand-600 hover:text-brand-800"
                onClick={() => setMixedPayments((prev) => [...prev, { paymentMethod: "TARJETA", amount: "" }])}
              >
                + Agregar otro método
              </button>
              <div className="flex justify-between border-t border-blue-100 pt-2 text-xs">
                <span className="text-gray-600">Falta cubrir (antes de efectivo)</span>
                <span className="font-medium text-gray-900">{currency.format(mixedRemaining)}</span>
              </div>
              {mixedCashLine && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-600">Cambio a entregar</span>
                  <span className="font-semibold text-gray-900">{currency.format(mixedChange)}</span>
                </div>
              )}
              {!mixedValid && mixedPayments.length > 0 && (
                <p className="text-[11px] text-red-600">
                  {mixedPayments.length < 2
                    ? "Agrega al menos 2 métodos (si vas a usar solo uno, cancela el pago mixto)."
                    : mixedNonCashTotal > totals.total
                      ? "Los métodos distintos a efectivo no pueden superar el total (no hay vuelto electrónico)."
                      : "El efectivo declarado no alcanza a cubrir lo que falta."}
                </p>
              )}
            </div>
          )}

          {!showMixedPayment && !isCredit && (
            <div className="mt-2 space-y-1.5">
              <label className="block text-xs font-medium text-gray-600">Monto recibido</label>
              <input className="input-field text-sm" type="number" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} />
              {paymentMethod === "EFECTIVO" && (
                <div className="flex flex-wrap gap-1">
                  {suggestedCashAmounts(totals.total).map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className="rounded-md border border-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                      onClick={() => setAmountPaid(String(amount))}
                    >
                      {currency.format(amount)}
                    </button>
                  ))}
                </div>
              )}
              {paymentMethod === "EFECTIVO" && (
                <div className="rounded-md bg-gray-50 p-2 text-center">
                  <p className="text-[11px] text-gray-500">Cambio a entregar</p>
                  <p className="text-lg font-semibold text-gray-900">{currency.format(change)}</p>
                </div>
              )}
            </div>
          )}

          {!showMixedPayment && isCredit && (
            <div className="mt-2 space-y-2 rounded-md bg-amber-50 p-2.5">
              <label className="block text-xs font-medium text-gray-700">Abono inicial (opcional)</label>
              <input className="input-field text-sm" type="number" value={amountPaid} onChange={(e) => setAmountPaid(e.target.value)} />
              <p className="text-xs font-medium text-amber-800">Saldo a crédito: {currency.format(Math.max(0, totals.total - Number(amountPaid || 0)))}</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700">N.° de cuotas</label>
                  <input className="input-field mt-1 text-sm" type="number" min={1} max={36} value={creditInstallments} onChange={(e) => setCreditInstallments(e.target.value)} />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700">Días entre cuotas</label>
                  <input className="input-field mt-1 text-sm" type="number" min={1} max={90} value={creditIntervalDays} onChange={(e) => setCreditIntervalDays(e.target.value)} />
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700">Vencimiento de la primera cuota</label>
                <input className="input-field mt-1 text-sm" type="date" value={creditFirstDueDate} onChange={(e) => setCreditFirstDueDate(e.target.value)} />
              </div>
            </div>
          )}

          <button type="button" className="btn-primary relative mt-3 w-full" disabled={!checkoutValid || createSale.isPending} onClick={handleCharge}>
            {createSale.isPending ? "Registrando..." : `Cobrar ${currency.format(totals.total)}`}
            <span className="absolute right-3 hidden rounded border border-white/30 px-1.5 py-0.5 text-[10px] sm:inline">F8</span>
          </button>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button type="button" className="btn-secondary text-xs" disabled={cart.length === 0} onClick={handleSuspendSale}>
              <Save size={13} className="mr-1" /> Guardar venta
            </button>
            <button type="button" className="btn-secondary text-xs text-red-600" disabled={cart.length === 0} onClick={resetCheckoutFields}>
              <Eraser size={13} className="mr-1" /> Limpiar carrito
            </button>
          </div>

          <div className="mt-3 border-t border-gray-100 pt-3">
            <label className="mb-1 flex items-center justify-between text-xs font-medium text-gray-600">
              Cliente (opcional)
              <span className="hidden rounded border border-gray-200 px-1 py-0.5 text-[10px] text-gray-400 sm:inline">F4</span>
            </label>
            {customerId ? (
              <div className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                <span className="truncate text-gray-900">{customerName}</span>
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-brand-600 hover:text-brand-800"
                  onClick={() => {
                    setCustomerId(null);
                    setCustomerName("");
                  }}
                >
                  Cambiar
                </button>
              </div>
            ) : (
              <div className="relative flex gap-1.5">
                <input
                  ref={customerInputRef}
                  className="input-field flex-1 text-sm"
                  placeholder="Buscar cliente o cédula..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                />
                <button type="button" className="btn-secondary px-2.5" title="Nuevo cliente">
                  <UserPlus size={14} />
                </button>
                {customerSearch.length >= 2 && (customerMatches?.length ?? 0) > 0 && (
                  <div className="absolute left-0 right-9 top-full z-10 mt-1 max-h-40 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
                    {customerMatches!.map((c) => (
                      <button key={c.id} type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50" onClick={() => selectCustomer(c)}>
                        {c.name} {c.documentId ? `(${c.documentId})` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-gray-500">
            <span>
              Caja actual <br /> <strong className="text-gray-700">{branchName.toUpperCase()}</strong>
            </span>
            <span className="text-right">
              Vendedor <br /> <strong className="text-gray-700">{user?.fullName ?? "—"}</strong>
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-3 text-xs text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <Keyboard size={13} /> Atajos de teclado: <strong className="text-gray-500">F2</strong> Buscar · <strong className="text-gray-500">F4</strong> Cliente ·{" "}
          <strong className="text-gray-500">F8</strong> Cobrar · <strong className="text-gray-500">ESC</strong> Cancelar
        </span>
      </div>

      {showCloseShift && <CloseShiftModal shiftId={shiftId} onClose={() => setShowCloseShift(false)} />}
      {showCashMovement && <CashMovementModal shiftId={shiftId} onClose={() => setShowCashMovement(false)} />}
      {showBelowCostConfirm && (
        <BelowCostConfirmModal
          lines={belowCostLines}
          isPending={createSale.isPending}
          onCancel={() => setShowBelowCostConfirm(false)}
          onConfirm={() => submitSale(true)}
        />
      )}
    </div>
  );
}

function BelowCostConfirmModal({
  lines,
  isPending,
  onCancel,
  onConfirm,
}: {
  lines: Array<{ product: ProductRecord; effectiveUnitPrice: number; unitCost: number }>;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
          <TrendingDown className="text-red-600" size={18} />
          <h2 className="text-sm font-semibold text-gray-900">Vas a vender por debajo del costo</h2>
        </div>
        <div className="space-y-3 p-4">
          <p className="text-sm text-gray-600">
            {lines.length === 1 ? "Este producto tiene" : "Estos productos tienen"} un precio efectivo menor a su costo registrado. Puede ser una decisión válida (liquidación, producto por vencer, etc.), pero confirma antes de continuar.
          </p>
          <div className="space-y-1.5 rounded-md bg-red-50 p-3 text-sm">
            {lines.map((l) => (
              <div key={l.product.id} className="flex items-center justify-between">
                <span className="text-gray-800">{l.product.name}</span>
                <span className="font-medium text-red-700">
                  {currency.format(l.effectiveUnitPrice)} <span className="text-gray-400">/ costo {currency.format(l.unitCost)}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={isPending}>
            Cancelar
          </button>
          <button type="button" className="btn-primary bg-red-600 hover:bg-red-700" onClick={onConfirm} disabled={isPending}>
            {isPending ? "Cobrando..." : "Vender de todas formas"}
          </button>
        </div>
      </div>
    </div>
  );
}

function pct(part: number, total: number): string {
  if (!total) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
}

const STAT_COLORS: Record<string, string> = {
  brand: "bg-brand-50 text-brand-600",
  green: "bg-green-50 text-green-600",
  blue: "bg-blue-50 text-blue-600",
  purple: "bg-purple-50 text-purple-600",
  amber: "bg-amber-50 text-amber-600",
  red: "bg-red-50 text-red-600",
  teal: "bg-teal-50 text-teal-600",
};

function StatCard({ icon: Icon, color, label, value, sub }: { icon: typeof Banknote; color: string; label: string; value: string; sub: string }) {
  return (
    <div className="card flex items-center gap-2.5 p-3">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${STAT_COLORS[color]}`}>
        <Icon size={16} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-[11px] text-gray-500">{label}</p>
        <p className="truncate text-sm font-semibold text-gray-900">{value}</p>
        <p className="truncate text-[10px] text-gray-400">{sub}</p>
      </div>
    </div>
  );
}

function CloseShiftModal({ shiftId, onClose }: { shiftId: string; onClose: () => void }) {
  const closeShift = useCloseCashShift();
  const { data: breakdown } = useCashShiftBreakdown(shiftId);
  const [closingCash, setClosingCash] = useState("0");

  const handleSubmit = async () => {
    try {
      const result = await closeShift.mutateAsync({ id: shiftId, closingCash });
      toast.success(`Turno cerrado. Diferencia: ${currency.format(Number(result.difference ?? 0))}`);
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-sm flex-col rounded-lg bg-white shadow-xl">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Cerrar turno de caja</h2>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {breakdown && breakdown.byPaymentMethod.length > 0 && (
            <div className="rounded-md bg-gray-50 p-3">
              <p className="mb-1.5 text-xs font-semibold uppercase text-gray-500">Desglose por método de pago</p>
              <ul className="space-y-1 text-sm">
                {breakdown.byPaymentMethod.map((row) => (
                  <li key={row.paymentMethod} className="flex justify-between">
                    <span className="text-gray-600">{PAYMENT_METHOD_LABEL[row.paymentMethod as PaymentMethod] ?? row.paymentMethod}</span>
                    <span className="font-medium text-gray-900">
                      {currency.format(row.salesTotal)}
                      {row.refundsTotal > 0 && <span className="ml-1 text-xs font-normal text-red-500">(-{currency.format(row.refundsTotal)} dev.)</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-1.5 flex justify-between border-t border-gray-200 pt-1.5 text-sm font-semibold">
                <span className="text-gray-700">Total vendido</span>
                <span className="text-gray-900">{currency.format(breakdown.grandSalesTotal)}</span>
              </div>
            </div>
          )}
          <label className="block text-sm font-medium text-gray-700">Efectivo contado en caja</label>
          <input className="input-field" type="number" value={closingCash} onChange={(e) => setClosingCash(e.target.value)} />
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={closeShift.isPending} onClick={handleSubmit}>
            {closeShift.isPending ? "Cerrando..." : "Cerrar turno"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Entrada/salida manual de efectivo del turno actual — dinero que sale o
 * entra a la caja física sin ser una venta ni una devolución (ej. un
 * repartidor cobra en efectivo un domicilio, o se agrega cambio extra).
 * Antes no existía forma de registrar esto: el cierre de caja quedaba con
 * una diferencia sin explicación. Se suma/resta al efectivo esperado al
 * cerrar el turno (ver pos.service.ts::closeCashShift).
 */
function CashMovementModal({ shiftId, onClose }: { shiftId: string; onClose: () => void }) {
  const { data: movements, isLoading } = useCashMovements(shiftId);
  const registerMovement = useRegisterCashMovement();
  const [type, setType] = useState<CashMovementType>("SALIDA");
  const [amount, setAmount] = useState("");
  const [concept, setConcept] = useState("");
  const [notes, setNotes] = useState("");

  const isValid = Number(amount) > 0 && concept.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) {
      toast.error("Ingresa un monto mayor a cero y el motivo del movimiento.");
      return;
    }
    try {
      await registerMovement.mutateAsync({ cashShiftId: shiftId, type, amount, concept, notes: notes || undefined });
      toast.success(type === "SALIDA" ? "Salida de caja registrada." : "Entrada de caja registrada.");
      setAmount("");
      setConcept("");
      setNotes("");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-md flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-900">Entrada / salida de caja</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <Ban size={18} />
          </button>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${type === "SALIDA" ? "border-red-300 bg-red-50 text-red-700" : "border-gray-200 text-gray-500"}`}
              onClick={() => setType("SALIDA")}
            >
              <TrendingDown size={14} className="mr-1 inline" /> Salida (retiro)
            </button>
            <button
              type="button"
              className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium ${type === "ENTRADA" ? "border-green-300 bg-green-50 text-green-700" : "border-gray-200 text-gray-500"}`}
              onClick={() => setType("ENTRADA")}
            >
              <TrendingUp size={14} className="mr-1 inline" /> Entrada
            </button>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Monto</label>
            <input className="input-field mt-1" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Motivo</label>
            <input
              className="input-field mt-1"
              value={concept}
              onChange={(e) => setConcept(e.target.value)}
              placeholder={type === "SALIDA" ? "Ej: Pago de domicilio en efectivo" : "Ej: Cambio adicional"}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
            <input className="input-field mt-1" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button type="button" className="btn-primary w-full" disabled={!isValid || registerMovement.isPending} onClick={handleSubmit}>
            {registerMovement.isPending ? "Guardando..." : "Registrar movimiento"}
          </button>

          <div className="border-t border-gray-100 pt-3">
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400">Movimientos de este turno</p>
            {isLoading && <p className="text-sm text-gray-400">Cargando...</p>}
            {!isLoading && (!movements || movements.length === 0) && <p className="text-sm text-gray-400">Sin movimientos todavía.</p>}
            <div className="divide-y divide-gray-100">
              {movements?.map((m) => (
                <div key={m.id} className="flex items-center justify-between py-1.5 text-sm">
                  <div>
                    <p className="text-gray-900">{m.concept}</p>
                    <p className="text-xs text-gray-400">
                      {CASH_MOVEMENT_TYPE_LABEL[m.type]} · {m.createdBy?.fullName ?? "—"} · {new Date(m.createdAt).toLocaleTimeString("es-CO")}
                    </p>
                  </div>
                  <span className={`font-medium ${m.type === "SALIDA" ? "text-red-600" : "text-green-600"}`}>
                    {m.type === "SALIDA" ? "-" : "+"}
                    {currency.format(Number(m.amount))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
